import React, { useEffect, useRef, useState } from "react";
import { Column } from "components/Layout/Grid";
import ManagementPanelModalCard from "components/Modal/ManagementPanelModalCard";
import Table from "components/Table";
import ManagementPanelService from "services/managementPanel.service";
import { IChangeRateRow, changeRateTableModel } from "./changeRateTableModel";

const PAGE_SIZE = 10;

const MOCK_CHANGE_RATE_BY_SELLER_RESPONSE = {
  data: [] as unknown[],
  total: 0,
};

function mapApiItemToRow(item: any, index: number): IChangeRateRow {
  const bu =
    item.businessUnitIdentifier ??
    "";
  const rep =
    item.sellerName ??
    "";
  const sellerCode =
    item.sellerCode ??
    "";
  const rawRate = item.changeRate;
  const rate = typeof rawRate === "number" ? rawRate : Number(rawRate);

  return {
    id: String(item.id ?? item.sellerCode ?? `${index}-${bu}-${rep}`),
    businessUnit: String(bu || "-"),
    representative: String(rep || ""),
    sellerCode: String(sellerCode || ""),
    rate: Number.isFinite(rate) ? rate : 0,
  };
}

export interface ModalChangeRateProps {
  open: boolean;
  onClose: () => void;
  filterCurrent: Record<string, any>;
}

export const ModalChangeRate: React.FC<ModalChangeRateProps> = ({ open, onClose, filterCurrent }) => {
  const _managementPanelService = new ManagementPanelService();

  const searchValueRef = useRef("");
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<IChangeRateRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [tableOrder, setTableOrder] = useState("");

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  async function loadChangeRateBySeller(page: number = 0) {
    setIsLoading(true);
    try {
      const searchTerm = searchValueRef.current.trim();
      const filterMapping = {
        ProductCategories: filterCurrent.ProductCategories,
        SellerCodes: filterCurrent.SellerCodes,
        Origin: filterCurrent.Origin,
        BusinessUnitIdentifier: filterCurrent.BusinessUnitIdentifier,
        StartDate: filterCurrent.StartDate,
        EndDate: filterCurrent.EndDate,
        page: page + 1,
        pageSize: PAGE_SIZE,
        ...(searchTerm ? { search: searchTerm } : {}),
      };

      let response: any;
      try {
        response = await _managementPanelService.getChangeRateBySeller(filterMapping);
      } catch {
        response = MOCK_CHANGE_RATE_BY_SELLER_RESPONSE;
      }

      if (response?.error) {
        setTableData([]);
        setTotalCount(0);
        setCurrentPage(page);
        return;
      }

      const rawData = response?.data ?? response ?? [];
      const total = response?.total ?? (Array.isArray(rawData) ? rawData.length : 0);
      const rows: IChangeRateRow[] = Array.isArray(rawData)
        ? rawData.map((item: any, i: number) => mapApiItemToRow(item, i))
        : [];

      setTableData(rows);
      setTotalCount(typeof total === "number" ? total : rows.length);
      setCurrentPage(page);
    } catch {
      setTableData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  function handleKeyDown(key: string) {
    if (key === "Enter") {
      setCurrentPage(0);
      loadChangeRateBySeller(0);
    }
  }

  function handlePageChange(newPage: number) {
    setCurrentPage(newPage);
    loadChangeRateBySeller(newPage);
  }

  if (!open) return null;

  return (
    <ManagementPanelModalCard
      title="Taxa de alteração"
      searchFieldLabel="Buscar BU ou representante"
      width="700px"
      isLoading={isLoading}
      onSearchSubmit={(value) => {
        searchValueRef.current = value;
      }}
      onCloseAction={onClose}
      onKeyDown={(e) => handleKeyDown(e)}
      onOpen={() => {
        searchValueRef.current = "";
        setCurrentPage(0);
        loadChangeRateBySeller(0);
      }}
    >
      <Column className="w-100 change-rate-modal-table-wrapper px-1 pl-sm-3 pl-md-3 pl-lg-3 mt-4">
        <Table
          trackingKey="Taxa de alteração - Table"
          model={changeRateTableModel}
          data={tableData}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          setTableOrder={setTableOrder}
          tableOrder={tableOrder}
          showPagination={true}
          portalCustomHeader
          className="w-100 change-rate-modal-table"
          emptyDataMessage="Nenhum registro encontrado"
        />
      </Column>
    </ManagementPanelModalCard>
  );
};
